// Browser-safe: imported by the frontend via @vivport/backend/types. No server imports.

export const CONTENT_IMAGE_REF_PREFIX = 'content:';

// A document's layout comes from the Template row (definition roots and instances resolve it
// through readDocumentLayoutTrait); nothing carries traits inside bodies any more.
export type DocumentLayout = 'letter' | 'sectioned';

export function isHtmlCommentOnly(raw: string): boolean {
  let rest = raw.trim();
  if (rest.length === 0) return false;
  while (rest.startsWith('<!--')) {
    const end = rest.indexOf('-->');
    if (end === -1) return false;
    rest = rest.slice(end + 3).trim();
  }
  return rest.length === 0;
}

// The image-expected token: a cell or paragraph carrying exactly this text means "a photo
// belongs here". Storage keeps the literal token; every rendering surface swaps it for an
// image glyph. Named the slot, not just its presence: a template author reading a body has to
// be able to tell "a photo belongs in this cell" from an ordinary [Date]-style placeholder.
export const PHOTO_PLACEHOLDER_TOKEN = '[photo]';

const IMAGE_REF_SYNTAX = /!\[[^\]]*\]\([^)]*\)/g;

export function isDividerTableRowCells(cells: readonly string[]): boolean {
  if (cells.length < 2) return false;
  const firstVisible = cells[0].replace(IMAGE_REF_SYNTAX, '').trim();
  if (firstVisible.length === 0) return false;
  // A ghost row whose photo column is the FIRST column looks exactly like a band row
  // (first cell filled, rest empty) — the token exempts it, so it stays an ordinary row.
  if (firstVisible === PHOTO_PLACEHOLDER_TOKEN) return false;
  return cells.slice(1).every((cell) => cell.trim().length === 0);
}

export interface TableCellImageRef {
  code: string;
  raw: string;
}

export interface MarkdownTableCellIndex {
  tableIndex: number;
  rowIndex: number;
  columnIndex: number;
}

export interface MarkdownTableImagePlacement extends MarkdownTableCellIndex {
  code: string;
  raw: string;
}

/** The coordinates of a table-image move. Shared verbatim by the tool schema, the route, and the UI. */
export interface MoveDocumentTableImageInput {
  documentId: number;
  imageCode: string;
  tableIndex: number;
  fromRowIndex: number;
  fromColumnIndex: number;
  toRowIndex: number;
  toColumnIndex: number;
}

export interface MoveDocumentTableRowInput {
  documentId: number;
  tableIndex: number;
  fromRowIndex: number;
  toRowIndex: number;
}

export interface MarkdownTableLocation {
  tableIndex: number;
  headerLine: number;
  separatorLine: number;
  /** Absolute line indices of the body rows, in document order, INCLUDING divider rows. */
  bodyLines: number[];
}

export interface TableRowCellSpan {
  /** Offsets into the row's source line, so a cell can be spliced without reformatting the rest. */
  start: number;
  end: number;
  text: string;
}

const TABLE_SEPARATOR_CELL = /^\s*:?-+:?\s*$/;

function isTableRowLine(line: string): boolean {
  return line.trim().startsWith('|');
}

/**
 * Cell spans of one table row line. Leading and trailing pipes are GFM delimiters, not cells,
 * so they are dropped positionally — never by emptiness, which would swallow a genuinely blank
 * first cell. A `\|` is literal content and does not split.
 */
export function tableRowCellSpans(line: string): TableRowCellSpan[] {
  const spans: TableRowCellSpan[] = [];
  let start = 0;
  for (let i = 0; i < line.length; i++) {
    if (line[i] === '\\' && i + 1 < line.length && line[i + 1] === '|') {
      i++;
      continue;
    }
    if (line[i] !== '|') continue;
    spans.push({ start, end: i, text: line.slice(start, i) });
    start = i + 1;
  }
  spans.push({ start, end: line.length, text: line.slice(start) });

  const trimmed = line.trim();
  if (trimmed.startsWith('|')) spans.shift();
  if (spans.length > 0 && trimmed.endsWith('|') && !trimmed.endsWith('\\|')) spans.pop();
  return spans;
}

export function splitTableRowCells(line: string): string[] {
  return tableRowCellSpans(line).map((span) => span.text);
}

function isSeparatorLine(line: string): boolean {
  if (!isTableRowLine(line)) return false;
  const cells = splitTableRowCells(line);
  return cells.length > 0 && cells.every((cell) => TABLE_SEPARATOR_CELL.test(cell));
}

/**
 * Every GFM table in a document body, in document order. A table is a row line followed by a
 * separator line; its body is the unbroken run of row lines after it.
 */
export function locateMarkdownTables(body: string): MarkdownTableLocation[] {
  const lines = body.split('\n');
  const tables: MarkdownTableLocation[] = [];
  for (let i = 0; i < lines.length - 1; i++) {
    if (!isTableRowLine(lines[i]) || isSeparatorLine(lines[i])) continue;
    if (!isSeparatorLine(lines[i + 1])) continue;
    const bodyLines: number[] = [];
    let cursor = i + 2;
    while (cursor < lines.length && isTableRowLine(lines[cursor]) && !isSeparatorLine(lines[cursor])) {
      bodyLines.push(cursor);
      cursor++;
    }
    tables.push({ tableIndex: tables.length, headerLine: i, separatorLine: i + 1, bodyLines });
    i = cursor - 1;
  }
  return tables;
}

// Plain text a table cell reads as: content images become the word photo, other images and
// links keep their text, inline emphasis and code marks are dropped, line breaks become spaces.
export function flattenCellMarkdown(cell: string): string {
  return cell
    .replace(/!\[[^\]]*\]\(\s*content:[^)]*\)/g, 'photo')
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/(\*\*|__|~~|`)/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function tableCellImageRefs(cell: string): TableCellImageRef[] {
  const refs: TableCellImageRef[] = [];
  for (const match of cell.matchAll(/!\[[^\]]*\]\(([^)]*)\)/g)) {
    const href = match[1].trim();
    if (!href.startsWith(CONTENT_IMAGE_REF_PREFIX)) continue;
    const code = href.slice(CONTENT_IMAGE_REF_PREFIX.length).trim();
    if (code.length > 0) refs.push({ code, raw: match[0] });
  }
  return refs;
}

/**
 * The one indexing contract the document page and the move logic both obey: row indices are
 * 0-based over the rows AFTER the separator, in document order, INCLUDING divider rows.
 */
export function indexMarkdownTableImages(body: string): MarkdownTableImagePlacement[] {
  const lines = body.split('\n');
  const placements: MarkdownTableImagePlacement[] = [];
  for (const table of locateMarkdownTables(body)) {
    table.bodyLines.forEach((lineIndex, rowIndex) => {
      splitTableRowCells(lines[lineIndex]).forEach((cell, columnIndex) => {
        for (const ref of tableCellImageRefs(cell)) {
          placements.push({
            tableIndex: table.tableIndex,
            rowIndex,
            columnIndex,
            code: ref.code,
            raw: ref.raw,
          });
        }
      });
    });
  }
  return placements;
}
